import { type ReactNode, useRef } from 'react'
import {
  motion,
  useMotionValue,
  useReducedMotion,
  useSpring,
  useTransform,
} from 'framer-motion'

type HeroCollageTiltProps = {
  children: ReactNode
}

/** Subtle perspective tilt on pointer move — “3D” depth without WebGL */
export function HeroCollageTilt({ children }: HeroCollageTiltProps) {
  const ref = useRef<HTMLDivElement>(null)
  const reduced = useReducedMotion()
  const mx = useMotionValue(0)
  const my = useMotionValue(0)

  const spring = { stiffness: 140, damping: 22, mass: 0.35 }
  const rotateX = useSpring(
    useTransform(my, [-0.5, 0.5], [4.5, -4.5]),
    spring
  )
  const rotateY = useSpring(
    useTransform(mx, [-0.5, 0.5], [-5, 5]),
    spring
  )

  function onMove(e: React.MouseEvent<HTMLDivElement>) {
    if (!ref.current || reduced) return
    const r = ref.current.getBoundingClientRect()
    if (r.width === 0 || r.height === 0) return
    mx.set((e.clientX - r.left) / r.width - 0.5)
    my.set((e.clientY - r.top) / r.height - 0.5)
  }

  function onLeave() {
    mx.set(0)
    my.set(0)
  }

  if (reduced) {
    return <div className="relative">{children}</div>
  }

  return (
    <div
      className="relative [perspective:1200px]"
      onMouseMove={onMove}
      onMouseLeave={onLeave}
    >
      <motion.div
        ref={ref}
        className="relative transform-gpu [transform-style:preserve-3d]"
        style={{
          rotateX,
          rotateY,
        }}
      >
        {children}
      </motion.div>
    </div>
  )
}
